import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { MagnifyingGlassIcon } from "@heroicons/react/20/solid";
import Loading from "../../components/loading";
import Pagination from "../../components/Pagination";
import AkfaMappingModal from "../../components/akfa/mapping/AkfaMappingModal";
import AkfaCreateFromAkfaModal from "../../components/akfa/mapping/AkfaCreateFromAkfaModal";
import AkfaMappingExcelImportModal from "../../components/akfa/mapping/AkfaMappingExcelImportModal";
import { AKFA_PAGE_BASE, getAkfaProducts, getAkfaWinproProducts } from "../../services/akfa/akfaService";
import { AKFA_PAGE_SIZE, formatDate, formatPrice, showError, toPageState } from "./akfaUtils";
import useFillViewportHeight from "./useFillViewportHeight";

// Spetsifikatsiya: FRONTEND_Plan_4.md — ikki sub-tab (WinPro / Akfa) + Mapping modali (DnD, batch saqlash).
// Bog'lash faqat modal orqali; Mapping tabida narx faqat ko'rinadi (tasdiqlash — "Narx o'zgarishlari" tabida).

const LINK_FILTERS = ["all", "mapped", "unmapped"];

// "Akfa tovarlari" filtri → GET /v1/akfa/products query parametrlari
const AKFA_LINK_QUERY = {
    all: {},
    mapped: { mappingStatus: "MAPPED" },
    unmapped: { mappingStatus: "NEW" },
};

const BADGE_TONES = {
    gray: ["bg-gray-100 text-gray-500", "bg-gray-400"],
    green: ["bg-green-50 text-green-700", "bg-green-600"],
    yellow: ["bg-yellow-50 text-yellow-700", "bg-yellow-500"],
};

const EMPTY_PAGE = { items: [], total: 0, pageCount: 0 };

const INPUT_CLASS =
    "border border-gray-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-indigo-500";

const TH_CLASS =
    "sticky top-0 z-[1] bg-slate-800 text-slate-50 text-left px-3 py-2.5 text-xs font-semibold uppercase tracking-wide whitespace-nowrap";

function classNames(...classes) {
    return classes.filter(Boolean).join(" ");
}

// Akfa tovariga ulangan teksturalar soni (faqat bog'lanish holati — priceStatus bu yerda ko'rsatilmaydi)
const akfaLinkCount = (item) =>
    item.linkedTextures?.length ?? item.mappedTextureCount ?? item.targets?.length ?? (item.mappingStatus === "MAPPED" ? 1 : 0);

function Badge({ tone, children }) {
    const [box, dot] = BADGE_TONES[tone] || BADGE_TONES.gray;
    return (
        <span
            className={classNames(
                "inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap",
                box
            )}
        >
            <span className={classNames("w-1.5 h-1.5 rounded-full flex-none", dot)} />
            {children}
        </span>
    );
}

function GroupPill({ children }) {
    return (
        <span className="bg-gray-100 text-gray-500 rounded-md px-2 py-0.5 text-xs whitespace-nowrap">
            {children || "—"}
        </span>
    );
}

function Toolbar({ label, filter, onFilterChange, searchInput, onSearchInput, onSearch }) {
    const { t } = useTranslation();
    return (
        <form onSubmit={onSearch} className="flex flex-wrap gap-2 p-3 border-b border-gray-200">
            <span className="basis-full text-xs font-semibold uppercase tracking-wide text-gray-400">{label}</span>
            <select value={filter} onChange={(e) => onFilterChange(e.target.value)} className={classNames(INPUT_CLASS, "min-w-[170px]")}>
                {LINK_FILTERS.map((key) => (
                    <option key={key} value={key}>
                        {t(`akfaIntegrationPage.mapping.filter_${key}`)}
                    </option>
                ))}
            </select>
            <div className="flex w-64 max-w-full min-w-0 items-center gap-1.5 border border-gray-300 rounded-md px-3 bg-white focus-within:ring-1 focus-within:ring-indigo-500">
                <MagnifyingGlassIcon className="w-4 h-4 flex-none text-gray-400" />
                <input
                    type="text"
                    value={searchInput}
                    onChange={(e) => onSearchInput(e.target.value)}
                    placeholder={t("akfaIntegrationPage.common.search_placeholder")}
                    className="w-full min-w-0 border-0 p-0 py-2 text-sm focus:ring-0"
                />
            </div>
            <button type="submit" className="px-4 py-2 bg-slate-800 text-white text-sm font-semibold rounded-md hover:bg-slate-700">
                {t("akfaIntegrationPage.common.search")}
            </button>
        </form>
    );
}

// "Yaratildi: <nom>" + "Mahsulotni ochish" (6 soniya)
function CreatedToast({ name, onOpen, closeToast }) {
    const { t } = useTranslation();
    return (
        <span>
            {t("akfaIntegrationPage.createFromAkfa.created", { name })}{" "}
            {onOpen && (
                <button
                    type="button"
                    onClick={() => {
                        closeToast?.();
                        onOpen();
                    }}
                    className="ml-2 font-bold underline"
                >
                    {t("akfaIntegrationPage.createFromAkfa.open_product")}
                </button>
            )}
        </span>
    );
}

export default function AkfaMappingTab({ refreshSummary }) {
    const { t } = useTranslation();
    const { token } = useSelector((state) => state.user);

    const [subTab, setSubTab] = useState("winpro");
    const [tableRef, tableHeight] = useFillViewportHeight([subTab]);

    const [akfaState, setAkfaState] = useState(EMPTY_PAGE);
    const [akfaLoading, setAkfaLoading] = useState(true);
    const [akfaPage, setAkfaPage] = useState(0);
    const [akfaFilter, setAkfaFilter] = useState("all");
    const [akfaSearchInput, setAkfaSearchInput] = useState("");
    const [akfaSearch, setAkfaSearch] = useState("");

    const [winproState, setWinproState] = useState(EMPTY_PAGE);
    const [winproLoading, setWinproLoading] = useState(true);
    const [winproPage, setWinproPage] = useState(0);
    const [winproFilter, setWinproFilter] = useState("all");
    const [winproSearchInput, setWinproSearchInput] = useState("");
    const [winproSearch, setWinproSearch] = useState("");

    // { mode: "product" | "global", product? } — Mapping modali
    const [mappingModal, setMappingModal] = useState(null);
    const [createFor, setCreateFor] = useState(null);
    const [excelImportOpen, setExcelImportOpen] = useState(false);

    const fetchAkfa = async () => {
        setAkfaLoading(true);
        const response = await getAkfaProducts(token, {
            ...AKFA_LINK_QUERY[akfaFilter],
            search: akfaSearch,
            page: akfaPage + AKFA_PAGE_BASE,
            size: AKFA_PAGE_SIZE,
        });
        if (response.success) {
            setAkfaState(toPageState(response.data, AKFA_PAGE_SIZE));
        } else {
            showError(response, t("akfaIntegrationPage.common.load_error"));
        }
        setAkfaLoading(false);
    };

    const fetchWinpro = async () => {
        setWinproLoading(true);
        const response = await getAkfaWinproProducts(token, {
            linkStatus: winproFilter,
            search: winproSearch,
            page: winproPage + AKFA_PAGE_BASE,
            size: AKFA_PAGE_SIZE,
        });
        if (response.success) {
            setWinproState(toPageState(response.data, AKFA_PAGE_SIZE));
        } else {
            showError(response, t("akfaIntegrationPage.common.load_error"));
        }
        setWinproLoading(false);
    };

    useEffect(() => {
        fetchAkfa();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [akfaPage, akfaFilter, akfaSearch]);

    useEffect(() => {
        fetchWinpro();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [winproPage, winproFilter, winproSearch]);

    const refreshAll = () => {
        fetchAkfa();
        fetchWinpro();
        refreshSummary();
    };

    const handleAkfaSearch = (e) => {
        e.preventDefault();
        setAkfaPage(0);
        setAkfaSearch(akfaSearchInput.trim());
    };

    const handleWinproSearch = (e) => {
        e.preventDefault();
        setWinproPage(0);
        setWinproSearch(winproSearchInput.trim());
    };

    const openProductModal = (item) =>
        setMappingModal({
            mode: "product",
            product: { productId: item.productId, productName: item.productName, article: item.article },
        });

    const handleCreated = (product) => {
        setCreateFor(null);
        refreshAll();
        const onOpen = product.productId != null ? () => setMappingModal({ mode: "product", product }) : null;
        toast.success(<CreatedToast name={product.productName} onOpen={onOpen} />, {
            position: "bottom-right",
            autoClose: 6000,
        });
    };

    const subTabs = [
        { key: "winpro", label: t("akfaIntegrationPage.mapping.subtab_winpro"), count: winproState.total },
        { key: "akfa", label: t("akfaIntegrationPage.mapping.subtab_akfa"), count: akfaState.total },
    ];

    return (
        <div className="py-4">
            <div className="flex items-center gap-6 border-b border-gray-200 mb-4 overflow-x-auto">
                <div role="tablist" className="flex gap-6">
                    {subTabs.map((tab) => (
                        <button
                            key={tab.key}
                            type="button"
                            role="tab"
                            aria-selected={subTab === tab.key}
                            onClick={() => setSubTab(tab.key)}
                            className={classNames(
                                "inline-flex items-center gap-2 py-2.5 px-0.5 border-b-2 text-sm whitespace-nowrap",
                                subTab === tab.key
                                    ? "border-indigo-600 text-gray-900 font-semibold"
                                    : "border-transparent text-gray-500 font-medium hover:text-gray-900"
                            )}
                        >
                            {tab.label}
                            <span className="font-mono text-xs bg-gray-100 text-gray-500 rounded-full px-2 py-px font-medium">
                                {tab.count}
                            </span>
                        </button>
                    ))}
                </div>
                <button
                    type="button"
                    onClick={() => setExcelImportOpen(true)}
                    className="ml-auto flex-none px-3.5 py-1.5 text-sm font-semibold text-gray-900 bg-white border border-gray-300 rounded-md hover:border-indigo-600 hover:text-indigo-600"
                >
                    {t("akfaIntegrationPage.mapping.import_excel")}
                </button>
                <button
                    type="button"
                    onClick={() => setMappingModal({ mode: "global" })}
                    className="flex-none px-3.5 py-1.5 text-sm font-semibold text-white bg-slate-800 rounded-md hover:bg-slate-700"
                >
                    {t("akfaIntegrationPage.mapping.add_mapping")}
                </button>
            </div>

            {subTab === "winpro" ? (
                <section role="tabpanel" className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                    <Toolbar
                        label={t("akfaIntegrationPage.mapping.subtab_winpro")}
                        filter={winproFilter}
                        onFilterChange={(value) => {
                            setWinproPage(0);
                            setWinproFilter(value);
                        }}
                        searchInput={winproSearchInput}
                        onSearchInput={setWinproSearchInput}
                        onSearch={handleWinproSearch}
                    />
                    <div ref={tableRef} style={{ height: tableHeight ?? undefined }} className="overflow-auto">
                        {winproLoading ? (
                            <Loading />
                        ) : (
                            <table className="min-w-full text-sm">
                                <thead>
                                    <tr>
                                        <th className={TH_CLASS}>{t("akfaIntegrationPage.mapping.col_winpro_product")}</th>
                                        <th className={TH_CLASS}>{t("akfaIntegrationPage.mapping.col_group")}</th>
                                        <th className={TH_CLASS}>{t("akfaIntegrationPage.mapping.col_price")}</th>
                                        <th className={TH_CLASS}>{t("akfaIntegrationPage.common.status_label")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {winproState.items.length > 0 ? (
                                        winproState.items.map((item) => (
                                            <tr
                                                key={item.productId}
                                                tabIndex={0}
                                                onClick={() => openProductModal(item)}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter" || e.key === " ") {
                                                        e.preventDefault();
                                                        openProductModal(item);
                                                    }
                                                }}
                                                className="border-b border-gray-200 cursor-pointer transition-colors hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-600"
                                            >
                                                <td className="px-3 py-2.5 align-top">
                                                    <span className="font-semibold text-gray-900">{item.productName}</span>
                                                    <span className="block font-mono text-xs text-gray-400 mt-0.5">
                                                        {item.article || "—"}
                                                        {item.unitName ? ` · ${item.unitName}` : ""}
                                                    </span>
                                                </td>
                                                <td className="px-3 py-2.5 align-top">
                                                    <GroupPill>{item.articleGroupType ? t(item.articleGroupType) : null}</GroupPill>
                                                </td>
                                                <td className="px-3 py-2.5 align-top font-mono whitespace-nowrap">
                                                    {formatPrice(item.price, item.currencySymbol)}
                                                </td>
                                                <td className="px-3 py-2.5 align-top">
                                                    {item.totalTextureCount > 0 ? (
                                                        <Badge
                                                            tone={
                                                                item.mappedTextureCount === item.totalTextureCount
                                                                    ? "green"
                                                                    : item.mappedTextureCount > 0
                                                                    ? "yellow"
                                                                    : "gray"
                                                            }
                                                        >
                                                            {t("akfaIntegrationPage.mapping.winpro_mapped_ratio", {
                                                                mapped: item.mappedTextureCount ?? 0,
                                                                total: item.totalTextureCount,
                                                            })}
                                                        </Badge>
                                                    ) : (
                                                        <span className="text-sm text-gray-400">—</span>
                                                    )}
                                                    {!item.hasPrice && (
                                                        <span className="block text-xs text-gray-400 mt-1">
                                                            {t("akfaIntegrationPage.mapping.no_price_yet")}
                                                        </span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={4} className="py-9 text-center text-sm text-gray-400">
                                                {t("akfaIntegrationPage.mapping.empty_winpro")}
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        )}
                    </div>
                </section>
            ) : (
                <section role="tabpanel" className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                    <Toolbar
                        label={t("akfaIntegrationPage.mapping.subtab_akfa")}
                        filter={akfaFilter}
                        onFilterChange={(value) => {
                            setAkfaPage(0);
                            setAkfaFilter(value);
                        }}
                        searchInput={akfaSearchInput}
                        onSearchInput={setAkfaSearchInput}
                        onSearch={handleAkfaSearch}
                    />
                    <div ref={tableRef} style={{ height: tableHeight ?? undefined }} className="overflow-auto">
                        {akfaLoading ? (
                            <Loading />
                        ) : (
                            <table className="min-w-full text-sm">
                                <thead>
                                    <tr>
                                        <th className={TH_CLASS}>{t("akfaIntegrationPage.mapping.col_akfa_product")}</th>
                                        <th className={TH_CLASS}>{t("akfaIntegrationPage.mapping.col_group")}</th>
                                        <th className={TH_CLASS}>{t("akfaIntegrationPage.mapping.col_akfa_price")}</th>
                                        <th className={TH_CLASS}>{t("akfaIntegrationPage.common.status_label")}</th>
                                        <th className={TH_CLASS}>{t("akfaIntegrationPage.mapping.col_updated")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {akfaState.items.length > 0 ? (
                                        akfaState.items.map((item) => {
                                            const count = akfaLinkCount(item);
                                            return (
                                                <tr key={item.id} className="border-b border-gray-200">
                                                    <td className="px-3 py-2.5 align-top">
                                                        <span className="font-semibold text-gray-900">{item.name}</span>
                                                        <span className="block font-mono text-xs text-gray-400 mt-0.5">
                                                            {item.externalId ?? "—"}
                                                            {item.article ? ` · ${item.article}` : ""}
                                                        </span>
                                                    </td>
                                                    <td className="px-3 py-2.5 align-top">
                                                        <GroupPill>{item.groupName}</GroupPill>
                                                    </td>
                                                    <td className="px-3 py-2.5 align-top font-mono whitespace-nowrap">
                                                        {formatPrice(item.currentRate, item.currentRateCurrency)}
                                                    </td>
                                                    <td className="px-3 py-2.5 align-top">
                                                        {count > 0 ? (
                                                            <Badge tone="green">
                                                                {count > 1
                                                                    ? t("akfaIntegrationPage.mapping.winpro_mapped", { count })
                                                                    : t("akfaIntegrationPage.mapping.filter_mapped")}
                                                            </Badge>
                                                        ) : (
                                                            <>
                                                                <Badge tone="gray">
                                                                    {t("akfaIntegrationPage.mapping.winpro_unmapped")}
                                                                </Badge>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setCreateFor(item)}
                                                                    className="block mt-1.5 px-2 py-0.5 text-xs font-semibold text-indigo-600 border border-indigo-600 rounded-md hover:bg-indigo-50"
                                                                >
                                                                    {t("akfaIntegrationPage.mapping.create_winpro")}
                                                                </button>
                                                            </>
                                                        )}
                                                    </td>
                                                    <td className="px-3 py-2.5 align-top text-xs text-gray-400 whitespace-nowrap">
                                                        {formatDate(item.rateUpdatedAt || item.lastSeenAt)}
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    ) : (
                                        <tr>
                                            <td colSpan={5} className="py-9 text-center text-sm text-gray-400">
                                                {t("akfaIntegrationPage.mapping.empty_akfa")}
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        )}
                    </div>
                </section>
            )}

            {subTab === "winpro" && winproState.pageCount > 1 && (
                <Pagination
                    handlePageClick={(e) => setWinproPage(e.selected)}
                    pageCount={winproState.pageCount}
                    forcePage={winproPage}
                />
            )}
            {subTab === "akfa" && akfaState.pageCount > 1 && (
                <Pagination
                    handlePageClick={(e) => setAkfaPage(e.selected)}
                    pageCount={akfaState.pageCount}
                    forcePage={akfaPage}
                />
            )}

            {mappingModal && (
                <AkfaMappingModal
                    key={mappingModal.mode === "global" ? "global" : `product-${mappingModal.product.productId}`}
                    mode={mappingModal.mode}
                    product={mappingModal.product}
                    onClose={() => setMappingModal(null)}
                    onSaved={refreshAll}
                />
            )}

            {createFor && (
                <AkfaCreateFromAkfaModal
                    akfaProduct={createFor}
                    onClose={() => setCreateFor(null)}
                    onCreated={handleCreated}
                />
            )}

            {excelImportOpen && (
                <AkfaMappingExcelImportModal onClose={() => setExcelImportOpen(false)} onImported={refreshAll} />
            )}
        </div>
    );
}
